import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Skeleton,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { MiniTimeline } from '../ui/MiniTimeline';
import { MonitorTypeBadge, type MonitorType } from '../ui/MonitorTypeBadge';

/**
 * Check data for timeline
 */
interface CheckData {
  status: 'UP' | 'DOWN';
  createdAt: Date | string;
}

/**
 * Monitor status type
 */
type MonitorStatus = 'UP' | 'DOWN' | 'PENDING' | 'PAUSED';

/**
 * Monitor data structure for the row
 */
export interface MonitorRowData {
  id: string;
  name: string;
  url: string;
  type: MonitorType;
  status: MonitorStatus;
  uptimePercentage: number;
  avgResponseTime: number | null;
  lastCheckedAt: Date | string | null;
  recentChecks?: CheckData[];
}

/**
 * MonitorRow component props
 */
interface MonitorRowProps {
  monitor: MonitorRowData;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onDelete?: (id: string) => void;
  isLoading?: boolean;
}

/**
 * Get status color
 */
function getStatusColor(status: MonitorStatus): string {
  switch (status) {
    case 'UP':
      return '#10B981';
    case 'DOWN':
      return '#EF4444';
    case 'PAUSED':
      return '#F59E0B';
    case 'PENDING':
    default:
      return '#6B7280';
  }
}

/**
 * Format relative time
 */
function formatRelativeTime(date: Date | string | null): string {
  if (!date) return '-';

  const now = new Date();
  const checkDate = new Date(date);
  const diffMs = now.getTime() - checkDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return '1d';
  return `${diffDays}d`;
}

/**
 * Format response time
 */
function formatResponseTime(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * MonitorRow loading skeleton
 */
export function MonitorRowSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Skeleton variant="circular" width={10} height={10} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="rounded" width={44} height={20} />
      </Box>
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={96} height={16} sx={{ display: { xs: 'none', md: 'block' } }} />
      <Skeleton variant="text" width={50} height={20} />
      <Skeleton variant="text" width={50} height={20} sx={{ display: { xs: 'none', sm: 'block' } }} />
      <Skeleton variant="text" width={30} height={20} sx={{ display: { xs: 'none', sm: 'block' } }} />
      <Skeleton variant="circular" width={28} height={28} />
    </Box>
  );
}

/**
 * MonitorRow component
 * Displays monitor information in a compact row format
 */
export function MonitorRow({
  monitor,
  onPause,
  onResume,
  onDelete,
  isLoading = false,
}: MonitorRowProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePause = () => {
    handleMenuClose();
    onPause?.(monitor.id);
  };

  const handleResume = () => {
    handleMenuClose();
    onResume?.(monitor.id);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(monitor.id);
  };

  if (isLoading) {
    return <MonitorRowSkeleton />;
  }

  const isPaused = monitor.status === 'PAUSED';
  const isDown = monitor.status === 'DOWN';
  const statusColor = getStatusColor(monitor.status);

  return (
    <>
      <Box
        component={RouterLink}
        to={`/monitors/${monitor.id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          textDecoration: 'none',
          color: 'inherit',
          transition: 'background-color 0.15s',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          ...(isDown && {
            backgroundColor: 'rgba(239, 68, 68, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
            },
          }),
          ...(isPaused && {
            opacity: 0.6,
          }),
        }}
      >
        {/* Status indicator */}
        <Tooltip title={monitor.status}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: statusColor,
              flexShrink: 0,
              boxShadow: isDown ? `0 0 0 3px rgba(239, 68, 68, 0.2)` : 'none',
            }}
          />
        </Tooltip>

        {/* Name and Type */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: { xs: 80, sm: 120 },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {monitor.name}
          </Typography>
          <MonitorTypeBadge type={monitor.type} size="small" />
        </Box>

        {/* Spacer */}
        <Box sx={{ flex: 1, minWidth: 0 }} />

        {/* Timeline */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0, width: 96 }}>
          <MiniTimeline checks={monitor.recentChecks || []} maxBars={24} />
        </Box>

        {/* Uptime */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            width: 52,
            textAlign: 'right',
            flexShrink: 0,
            color:
              monitor.uptimePercentage >= 99
                ? '#10B981'
                : monitor.uptimePercentage >= 95
                  ? '#F59E0B'
                  : '#EF4444',
          }}
        >
          {monitor.uptimePercentage.toFixed(1)}%
        </Typography>

        {/* Response time */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            width: 56,
            textAlign: 'right',
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {formatResponseTime(monitor.avgResponseTime)}
        </Typography>

        {/* Last check */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            width: 32,
            textAlign: 'right',
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {formatRelativeTime(monitor.lastCheckedAt)}
        </Typography>

        {/* Actions */}
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{
            ml: 0.5,
            opacity: 0.5,
            '&:hover': { opacity: 1 },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          component={RouterLink}
          to={`/monitors/${monitor.id}/edit`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        {isPaused ? (
          <MenuItem onClick={handleResume}>
            <ListItemIcon>
              <PlayArrowIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resume</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handlePause}>
            <ListItemIcon>
              <PauseIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Pause</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default MonitorRow;
